import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion, useScroll } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, CalendarDays, ListTree } from "lucide-react";
import SEO from "@/components/SEO";
import CTABand from "@/components/CTABand";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Container, Reveal, ImageReveal } from "@/components/Reveal";
import { getLenis } from "@/components/SmoothScroll";
import ArticleBlocks from "@/components/education/ArticleBlocks";
import { ArticleCard } from "@/components/education/ArticleCard";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getArticle, relatedArticleObjects } from "@/data/education";
import { SERVICES, INDUSTRIES, STATE_NAMES, COMPANY } from "@/data/content";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

function RelatedLinkList({ title, items }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">{title}</h3>
      <div className="mt-4 space-y-2">
        {items.map((it) => (
          <Link
            key={it.path}
            to={it.path}
            data-testid={it.testid}
            className="group flex items-center justify-between border-b border-line py-3 text-navy transition-colors hover:text-electric"
          >
            <span className="text-sm font-medium">{it.label}</span>
            <ArrowUpRight className="h-4 w-4 text-slatey transition-colors group-hover:text-electric" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export default function ArticleDetail() {
  const { slug } = useParams();
  const article = getArticle(slug);
  const { scrollYProgress } = useScroll();
  const [active, setActive] = useState("introduction");

  const toc = article
    ? [
        { id: "introduction", label: "Introduction" },
        ...article.sections.map((s) => ({ id: s.id, label: s.heading })),
        ...(article.faqs?.length ? [{ id: "faqs", label: "FAQs" }] : []),
        { id: "related", label: "Related" },
      ]
    : [];

  useEffect(() => {
    if (!article) return;
    const ids = toc.map((t) => t.id);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!article) return <Navigate to="/education" replace />;

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(el, { offset: -110 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  const related = relatedArticleObjects(article, 3);

  const solutions = (article.related?.solutions || [])
    .map((s) => SERVICES.find((x) => x.slug === s))
    .filter(Boolean)
    .map((s) => ({ label: s.title, path: `/solutions/${s.slug}`, testid: `related-solution-${s.slug}` }));
  const industries = (article.related?.industries || [])
    .map((s) => INDUSTRIES.find((x) => x.slug === s))
    .filter(Boolean)
    .map((i) => ({ label: i.name, path: `/industries/${i.slug}`, testid: `related-industry-${i.slug}` }));
  const states = (article.related?.states || [])
    .filter((s) => STATE_NAMES[s])
    .map((s) => ({ label: STATE_NAMES[s], path: `/service-areas/${s}`, testid: `related-state-${s}` }));

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.added,
    dateModified: article.updated,
    articleSection: article.categoryLabel,
    author: { "@type": "Organization", name: COMPANY.name },
    publisher: { "@type": "Organization", name: COMPANY.name },
    image: article.image,
  };

  return (
    <>
      <SEO title={article.title} description={article.excerpt} path={`/education/${slug}`} jsonLd={jsonLd} />

      {/* Reading progress */}
      <motion.div
        style={{ scaleX: scrollYProgress }}
        className="fixed inset-x-0 top-0 z-[60] h-0.5 origin-left bg-electric"
      />

      {/* Header */}
      <section className="relative overflow-hidden bg-navy pt-[74px] text-white md:pt-[88px]">
        <div className="grid-texture pointer-events-none absolute inset-0 opacity-50" />
        <Container className="relative py-16 md:py-20">
          <Breadcrumbs
            crumbs={[
              { label: "Education Center", path: "/education" },
              { label: article.categoryLabel, path: `/education/category/${article.category}` },
            ]}
            dark
          />
          <div className="mt-8 max-w-3xl">
            <div className="flex flex-wrap items-center gap-4 font-mono text-[11px] uppercase tracking-[0.16em] text-energy">
              {article.categoryLabel}
              <span className="flex items-center gap-1.5 text-white/50">
                <Clock className="h-3 w-3" /> {article.readingTime}
              </span>
              <span className="flex items-center gap-1.5 text-white/50">
                <CalendarDays className="h-3 w-3" /> Updated {fmtDate(article.updated)}
              </span>
            </div>
            <h1 className="mt-5 font-heading text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
              {article.title}
            </h1>
            <p className="mt-6 text-lg leading-relaxed text-white/70">{article.excerpt}</p>
          </div>
        </Container>
      </section>

      {/* Cover */}
      <div className="bg-white">
        <Container className="py-0">
          <ImageReveal
            src={article.image}
            alt={article.title}
            className="-mt-8 aspect-[16/9] border border-line md:-mt-12"
          />
        </Container>
      </div>

      {/* Body */}
      <article className="bg-white pb-24 pt-16 md:pb-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
            {/* TOC */}
            <aside className="lg:col-span-3">
              <div className="lg:sticky lg:top-28">
                <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slatey">
                  <ListTree className="h-4 w-4" /> On this page
                </div>
                <nav className="mt-4 space-y-1 border-l border-line" data-testid="article-toc">
                  {toc.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => goTo(t.id)}
                      className={
                        "-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-sm transition-colors " +
                        (active === t.id
                          ? "border-electric font-medium text-navy"
                          : "border-transparent text-slatey hover:text-navy")
                      }
                    >
                      {t.label}
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Content */}
            <div className="lg:col-span-8 lg:col-start-4">
              <div className="mx-auto max-w-3xl">
                <div id="introduction" className="scroll-mt-28">
                  <p className="text-xl leading-relaxed text-navy/80">{article.intro}</p>
                </div>

                {article.sections.map((s) => (
                  <section key={s.id} id={s.id} className="mt-14 scroll-mt-28">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
                      {s.heading}
                    </h2>
                    <div className="mt-5">
                      <ArticleBlocks blocks={s.blocks} />
                    </div>
                  </section>
                ))}

                {/* FAQ */}
                {article.faqs?.length > 0 && (
                  <section id="faqs" className="mt-16 scroll-mt-28">
                    <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
                      Frequently Asked Questions
                    </h2>
                    <Accordion type="single" collapsible className="mt-6 border-t border-line">
                      {article.faqs.map((f, i) => (
                        <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                          <AccordionTrigger className="py-5 text-left text-base text-navy hover:no-underline">
                            {f.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-slatey">{f.a}</AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </section>
                )}

                <div className="mt-14 border-t border-line pt-8">
                  <Link
                    to="/education"
                    className="inline-flex items-center gap-2 text-sm font-medium text-slatey transition-colors hover:text-navy"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back to Education Center
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </article>

      {/* Related content */}
      <section id="related" className="scroll-mt-28 bg-ice py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Related Content</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Continue learning.
          </h2>

          {related.length > 0 && (
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((a) => (
                <ArticleCard key={a.slug} article={a} testid={`related-article-${a.slug}`} />
              ))}
            </div>
          )}

          <div className="mt-14 grid grid-cols-1 gap-10 border-t border-line pt-12 md:grid-cols-3">
            <RelatedLinkList title="Related Solutions" items={solutions} />
            <RelatedLinkList title="Related Industries" items={industries} />
            <RelatedLinkList title="Related State Guides" items={states} />
          </div>
        </Container>
      </section>

      <CTABand
        heading="Turn knowledge into a strategy."
        text="Our advisors can apply these concepts to your specific facilities, contracts, and markets — with independent, transparent guidance."
        secondary={{ label: "Explore Solutions", to: "/solutions" }}
      />
    </>
  );
}
